import type { Alert } from '@api/api/event-alerts';
import type { DropdownEntry } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { usePostKgInsightSubscription } from '@terminal/react-query-hooks/events/use-event-alerts/usePostKgInsightSubscription';
import { usePostSubscription } from '@terminal/react-query-hooks/events/use-event-alerts/usePostSubscription';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useTranslation } from 'react-i18next';

interface UseTrackingDropdownItemArgs {
  alertEvent: Alert;
  isKgInsightCard: boolean;
  kgInsightEntityTag: string;
  trackingSource: PosthogTrackingSource;
}

export const useTrackingDropdownItem = ({
  alertEvent,
  isKgInsightCard,
  kgInsightEntityTag,
  trackingSource,
}: UseTrackingDropdownItemArgs): DropdownEntry | undefined => {
  const { t } = useTranslation('alerts', { useSuspense: false });
  const { mutate: toggleTracking } = usePostSubscription();
  const { mutate: toggleKgInsightTracking } = usePostKgInsightSubscription();
  const { trackAlertTracked, trackAlertUntracked } = usePosthogEvents();
  const { source_type: sourceType, tracking_status: trackingStatus } =
    alertEvent;
  const isTrackingActive = trackingStatus === 'active';
  const shouldShowTrackingOption =
    (sourceType === 'scenario-insight' || isKgInsightCard) &&
    !!trackingStatus &&
    trackingStatus !== 'completed';

  if (!shouldShowTrackingOption) {
    return undefined;
  }

  const handleTrackingClick = () => {
    const trackFn = isTrackingActive ? trackAlertUntracked : trackAlertTracked;

    if (isKgInsightCard) {
      trackFn(trackingSource, {
        id: alertEvent.id,
        source_id: kgInsightEntityTag,
        source_type: 'kg-insight',
        created_at: alertEvent.created_at,
      });
      toggleKgInsightTracking({
        entity_tag: kgInsightEntityTag,
        source_type: 'kg-insight',
        track: !isTrackingActive,
      });
      return;
    }

    trackFn(trackingSource, alertEvent);
    toggleTracking({
      source_id: alertEvent.source_id,
      source_type: 'scenario-insight',
      track: !isTrackingActive,
    });
  };

  return {
    label: isTrackingActive
      ? t('alerts:stopTracking')
      : t('alerts:resumeTracking'),
    key: 'tracking',
    onClick: handleTrackingClick,
  };
};
